/* eslint-disable */
import { Box } from '@mui/material';
import { Form, message, Spin } from 'antd';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import MainCard from 'components/MainCard';
import { toastConfig } from 'config';
import { isEqual } from 'lodash';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import CardAccordion from './CustomComponents/CardAccordion';
import LeaseDetailsShowAll from './CustomComponents/LeaseDetailsShowAll';
import { constantFormValues } from './root';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import ActionButtons from './CustomComponents/cardSections/ActionButtons';
import debounce from 'lodash/debounce';
import { stateUpdater, utcConverter } from './CustomComponents/LeaseUtils/helper';
import AgreementFiles from './CustomComponents/cardSections/AgreementFiles';
import GeneralLeaseInformation from './CustomComponents/cardSections/GeneralLeaseInformation';
import FinancialTermsConditions from './CustomComponents/cardSections/FinancialTermsConditions';
import IFRS16FieldsModifications from './CustomComponents/cardSections/IFRS16FieldsModifications';
import AccountingInformation from './CustomComponents/cardSections/AccountingInformation';
import LeasePageHeader from '../../console-solution/lease-schedule-v5/LeaseUtils/LeasePageHeader';
import LeaseHistory from './CustomComponents/LeaseUtils/History/LeaseHistory';
import LeaseSchedule from './CustomComponents/cardSections/LeaseSchedule';

dayjs.extend(utc);

const Edit = ({ screen = 'EDIT' }) => {
	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const [selectedTab, setSelectedTab] = useState(0);
	const [searchParams] = useSearchParams();
	const leaseDetailsShowAllRef = useRef();
	const handleDataLoadingExecutedRef = useRef(false);

	const [previousFormData, setPreviousFormData] = useState({});
	const [
		getSingleLeaseData,
		{
			data: singleLeaseData, //viewData
			isLoading: isViewLoading,
		},
	] = useGetSingleIdentityMutation();
	const statusData = useMemo(() => {
		return Object.keys(singleLeaseData?.parkedData ?? {})?.length === 0 ? singleLeaseData?.data : singleLeaseData?.parkedData;
	}, [singleLeaseData]);
	//constants
	dayjs.extend(utc);
	const [form] = Form.useForm();
	const { state } = useLocation();
	const navigate = useNavigate();

	// Combined States
	const [formState, setFormState] = useState({
		showFormSubmitModal: false,
		submitPayload: null,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});

	// Disable change tracking until data is fully loaded
	const [dataFullyLoaded, setDataFullyLoaded] = useState(false);

	// State to track original values for comparison
	const [originalValues, setOriginalValues] = useState({
		leaseTerm: null,
		initialDiscountRate: null,
		paymentFrequency: null,
		paymentModality: null,
		grossRents: null,
		advanceAmounts: null,
	});

	// Track if a change row has been added during this edit session
	const [changeRowAdded, setChangeRowAdded] = useState(false);

	// Keep track of the latest form values to prevent duplicate processing
	const lastProcessedValues = useRef({});

	// Track the original length of changes array for reverting
	const [originalChangesLength, setOriginalChangesLength] = useState(0);

	//simplified state setter
	const updateFormState = stateUpdater(setFormState);
	//Single States
	const [rowIndex, setRowIndex] = useState(0);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [updateApi, { isLoading: isUpdateLoading, error: updateError, isSuccess: isUpdateSuccess, isError: isUpdateError }] =
		useGetSingleIdentityMutation();

	// Fetch the data when component mounts
	useEffect(() => {
		// Reset tracking states on component mount
		setDataFullyLoaded(false);
		setChangeRowAdded(false);
		lastProcessedValues.current = {};

		getSingleLeaseData({
			suffixCode: '',
			_id: state?._id ?? '',
			operation: 'get-single',
			processCode: state?.processCode,
		});
	}, []);

	// Handle API responses and navigation
	useEffect(() => {
		if (isUpdateSuccess) {
			toast.success('Success! Record updated successfully', toastConfig);
			navigate(-2);
		}
		if (isUpdateError) {
			if (updateError?.data?.code === '501') {
				toast.error(updateError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isUpdateSuccess, updateError, isUpdateError]);

	// Set form values and initialize tracking when data loads
	useEffect(() => {
		// Skip if no data yet
		if (!statusData || Object.keys(statusData).length === 0) return;

		// console.log('Setting form data from statusData:', statusData);

		// Map form values from API data
		const viewValues = {
			// General Lease Information section
			tabIndex: '1',
			modificationSequence: statusData?.modificationSequence,
			leaseId: statusData?.leaseId,
			leaseType: statusData?.leaseType,
			leaseTitle: statusData?.leaseTitle,
			lessorName: statusData?.lessorName,
			paymentFrequency: statusData?.paymentFrequency,
			paymentModality: statusData?.paymentModality,
			creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,
			lastModifiedDate: dayjs(statusData?.lastModifiedDate),

			// Financial Terms & Conditions section
			advanceAmount: statusData?.advanceAmount,
			advanceDuration: statusData?.advanceDuration,
			grossRent: statusData?.grossRent,
			leaseTerm: statusData?.leaseTerm,
			leaseStartDate: dayjs(statusData?.leaseStartDate),
			leaseEndDate: dayjs(statusData?.leaseEndDate),
			escalationPercentage: 0,
			initialDiscountRate: statusData?.initialDiscountRate,
			upfrontLeasePayment: statusData?.upfrontLeasePayment,
			initialDirectCost: statusData?.initialDirectCost,
			securityDeposit: statusData?.securityDeposit,
			cashIncentiveAmount: statusData?.cashIncentiveAmount,
			endOfFinancialYear: dayjs(statusData?.endOfFinancialYear),

			//others
			version: String(singleLeaseData?.version),
			identityId: singleLeaseData?._id,
			leaseLiability: statusData?.leaseLiability,
			effectiveDateHistory: statusData?.effectiveDateHistory,
			leaseName: statusData?.leaseName,

			month: statusData?.month,

			agreementFiles: statusData?.agreementFiles,

			discountRateChanges: statusData?.discountRateChanges,
			advanceAmounts: statusData?.advanceAmounts,
			grossRents: statusData?.grossRents,
			grossRentList: statusData?.grossRentList,
			grossRentListSet: statusData?.grossRentListSet,
			advanceAmountListSet: statusData?.advanceAmountListSet,
			advanceAmountList: statusData?.advanceAmountList,
			//changes
			changes: statusData?.changes?.map((item) => {
				const { inputChangeEffectiveDate, ...change } = item;
				return {
					...change,
					inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
				};
			}),
		};

		// Store the original length of changes array
		setOriginalChangesLength(statusData?.changes?.length || 0);

		// Set row index based on existing changes
		setRowIndex(statusData?.changes?.length);

		// Set form values
		form.setFieldsValue(viewValues);

		// Wait for form to update before capturing original values
		// Use a longer timeout to ensure form fully processes all values
		setTimeout(() => {
			// Store key field values for change detection
			const originalFieldValues = {
				leaseTerm: form.getFieldValue('leaseTerm'),
				initialDiscountRate: form.getFieldValue('initialDiscountRate'),
				paymentFrequency: form.getFieldValue('paymentFrequency'),
				paymentModality: form.getFieldValue('paymentModality'),
				grossRents: form.getFieldValue('grossRents'), // Don't stringify, keep as original object
				advanceAmounts: form.getFieldValue('advanceAmounts'), // Don't stringify, keep as original object
			};

			// Also store these in the lastProcessedValues to avoid duplicate processing
			lastProcessedValues.current = { ...originalFieldValues };

			// Store the captured values
			setOriginalValues(originalFieldValues);
			// console.log('Original values captured:', originalFieldValues);

			// Mark data as fully loaded AFTER setting original values
			setDataFullyLoaded(true);
			// console.log('Data fully loaded, change tracking enabled');
		}, 500); // Use a longer timeout to ensure form is fully processed
	}, [statusData]);

	const [calculateData, setCalculateData] = useState({});
	const [selectHistory, setSelectHistory] = useState(null);

	const manageChangeRows = useCallback(() => {
		// Don't detect changes until data is fully loaded
		if (!dataFullyLoaded) {
			// console.log('Skipping change detection - data not loaded');
			return;
		}

		const currentChangeList = form.getFieldValue('changes') || [];
		const maxAllowedRows = originalChangesLength + 1;

		// If we already have more than max allowed rows, trim to the max
		if (currentChangeList.length > maxAllowedRows) {
			// console.log('Too many change rows detected, trimming to max allowed:', maxAllowedRows);
			const trimmedList = currentChangeList.slice(0, maxAllowedRows);
			form.setFieldsValue({ changes: trimmedList });
			setRowIndex(maxAllowedRows);
			// Update tracking state
			setChangeRowAdded(maxAllowedRows > originalChangesLength);
			return;
		}

		// Get current values from form
		const currentValues = {
			leaseTerm: form.getFieldValue('leaseTerm'),
			initialDiscountRate: form.getFieldValue('initialDiscountRate'),
			paymentFrequency: form.getFieldValue('paymentFrequency'),
			paymentModality: form.getFieldValue('paymentModality'),
			grossRents: form.getFieldValue('grossRents'),
			advanceAmounts: form.getFieldValue('advanceAmounts'),
		};

		// Check if we've already processed these exact values to prevent duplicate processing
		if (isEqual(currentValues, lastProcessedValues.current)) {
			// console.log('Values already processed, skipping');
			return;
		}

		// Update last processed values
		lastProcessedValues.current = { ...currentValues };

		// Deep compare values to detect changes
		const hasTermChanged = currentValues.leaseTerm !== originalValues.leaseTerm;
		const hasDiscountRateChanged = currentValues.initialDiscountRate !== originalValues.initialDiscountRate;
		const hasPaymentFrequencyChanged = currentValues.paymentFrequency !== originalValues.paymentFrequency;
		const hasPaymentModalityChanged = currentValues.paymentModality !== originalValues.paymentModality;

		// Use lodash isEqual for deep comparison of arrays
		const normalizeArray = (arr) => {
			if (!Array.isArray(arr)) return arr;
			return [...arr].map((v) => (typeof v === 'string' ? Number(v) || 0 : v || 0)).sort((a, b) => a - b);
		};

		const hasGrossRentsChanged = !isEqual(normalizeArray(currentValues.grossRents), normalizeArray(originalValues.grossRents));
		const hasAdvanceAmountsChanged = !isEqual(normalizeArray(currentValues.advanceAmounts), normalizeArray(originalValues.advanceAmounts));

		// Track if any fields changed
		const hasChanges =
			hasTermChanged ||
			hasDiscountRateChanged ||
			hasPaymentFrequencyChanged ||
			hasPaymentModalityChanged ||
			hasGrossRentsChanged ||
			hasAdvanceAmountsChanged;

		// Check if we should remove the added row when all changes are reverted
		if (!hasChanges && changeRowAdded && currentChangeList.length > originalChangesLength) {
			// console.log('All changes reverted, removing auto-added row');
			const updatedList = currentChangeList.slice(0, originalChangesLength);
			form.setFieldsValue({ changes: updatedList });
			setRowIndex(originalChangesLength);
			setChangeRowAdded(false);
			message.info('Changes reverted. The added change record has been removed.');
			return;
		}

		// If no changes detected, exit early
		if (!hasChanges) {
			return;
		}

		// Check if we need to handle an existing row vs create new one
		const hasAutoadedRow = currentChangeList.length > originalChangesLength;
		let shouldCreateNewRow = !hasAutoadedRow;

		if (hasAutoadedRow) {
			const lastRow = currentChangeList[currentChangeList.length - 1];
			// If the last row has an effective date set, don't create a new row
			if (lastRow && lastRow.inputChangeEffectiveDate) {
				shouldCreateNewRow = false;
			}
		}

		// Determine the appropriate change description
		let changeDescription = 'Initial Rent';
		if (hasGrossRentsChanged && hasAdvanceAmountsChanged) {
			changeDescription = 'Change In rent and advance amount';
		} else if (hasGrossRentsChanged) {
			changeDescription = 'Change In rent';
		} else if (hasAdvanceAmountsChanged) {
			changeDescription = 'Change In advance';
		}

		// Create the row data
		const newRow = {
			paymentFrequency: currentValues.paymentFrequency,
			paymentTime: currentValues.paymentModality,
			inputChangeLeaseTerm: currentValues.leaseTerm,
			inputChangeGrossAmount: 0,
			inputChangeDiscountRate: currentValues.initialDiscountRate,
			inputChangeEscalation: form.getFieldValue('escalationPercentage') || 0,
			inputChangeAdvance: form.getFieldValue('advanceAmount'),
			inputChangeAdvanceAmounts: currentValues.advanceAmounts,
			inputChangeGrossRents: currentValues.grossRents,
			inputChangeDescription: changeDescription,
		};

		if (shouldCreateNewRow) {
			// Only create a new row if we haven't already added one
			if (currentChangeList.length < maxAllowedRows) {
				newRow.inputChangeEffectiveDate = null;
				const updatedList = [...currentChangeList, newRow];
				form.setFieldsValue({ changes: updatedList });
				setRowIndex(updatedList.length);
				setChangeRowAdded(true);

				// Mark the effective date field as required
				requestAnimationFrame(() => {
					const newRowIndex = updatedList.length - 1;
					form.setFields([
						{
							name: ['changes', newRowIndex, 'inputChangeEffectiveDate'],
							errors: ['Effective Date is required'],
							touched: true,
						},
					]);
				});

				message.info('A new change record has been added. Please set the effective date.');
			}
		} else {
			// Update the existing added row
			const lastRowIndex = currentChangeList.length - 1;
			const lastRow = currentChangeList[lastRowIndex];

			// Preserve the effective date and other fields
			if (lastRow) {
				newRow.inputChangeEffectiveDate = lastRow.inputChangeEffectiveDate;
				if (lastRow.inputChangeMonth) {
					newRow.inputChangeMonth = lastRow.inputChangeMonth;
				}
			}

			const updatedList = [...currentChangeList];
			updatedList[lastRowIndex] = newRow;
			form.setFieldsValue({ changes: updatedList });

			// Notify if description changed
			if (lastRow && lastRow.inputChangeDescription !== changeDescription) {
				message.info(`Change type updated to "${changeDescription}"`);
			}
		}
	}, [form, originalValues, changeRowAdded, dataFullyLoaded, originalChangesLength, lastProcessedValues]);
	// Use a debounced version of the change handler to avoid rapid consecutive calls
	const debouncedManageChangeRows = useMemo(() => debounce(manageChangeRows, 300), [manageChangeRows]);
	function compareAndRemoveDuplicates(inputArray) {
		// Check if the array has at least 2 elements
		if (!Array.isArray(inputArray) || inputArray.length < 2) {
			return {
				data: inputArray,
				isChanged: false,
			};
		}

		// Get the last two elements
		const lastElement = inputArray[inputArray.length - 1];
		const secondLastElement = inputArray[inputArray.length - 2];

		// Function to normalize objects for comparison
		const normalizeForComparison = (arr) => {
			if (!Array.isArray(arr)) return arr;

			return arr.map((item) => {
				// Create a deep copy of the item
				const normalizedItem = JSON.parse(JSON.stringify(item));

				// Normalize each inner object
				return normalizedItem.map((obj) => {
					const result = { ...obj };

					// Normalize amount field if it exists - remove commas and standardize format
					if (obj.amount !== undefined && typeof obj.amount === 'string') {
						// Remove commas and convert to a number, then format back to string with 2 decimal places
						const numericValue = parseFloat(obj.amount.replace(/,/g, ''));
						if (!isNaN(numericValue)) {
							result.amount = numericValue.toFixed(2);
						}
					}

					return result;
				});
			});
		};

		// Normalize both arrays for comparison
		const normalizedLast = normalizeForComparison([lastElement])[0];
		const normalizedSecondLast = normalizeForComparison([secondLastElement])[0];

		// Deep compare the normalized elements
		const areEqual = JSON.stringify(normalizedLast) === JSON.stringify(normalizedSecondLast);

		if (areEqual) {
			// If they are the same, remove the last element
			const modifiedArray = inputArray.slice(0, -1);
			return {
				data: modifiedArray,
				isChanged: false,
			};
		} else {
			// If they are different, return the original array
			return {
				data: inputArray,
				isChanged: true,
			};
		}
	}
	const handleSubmit = async () => {
		const formData = form.getFieldsValue();
		let grossRentListSet = formData?.grossRentListSet || [];
		let advanceAmountListSet = formData?.advanceAmountListSet || [];
		let effectiveDateHistory = formData?.effectiveDateHistory || [];
		grossRentListSet?.push(formData?.grossRentList);
		// advanceAmountListSet?.push(formData?.advanceAmountList);
		const { data: grossRentListSetData, isChanged: grossRentListSetChanged } = compareAndRemoveDuplicates(grossRentListSet);

		if (grossRentListSetChanged) {
			effectiveDateHistory?.push(utcConverter(formData?.changes?.[formData?.changes?.length - 1]?.inputChangeEffectiveDate));
		}

		// Prepare all changes for submission
		const changesPayload = formData.changes?.map((change) => {
			// Ensure the effective date is in the correct format for API
			if (change.inputChangeEffectiveDate) {
				return {
					...change,
					inputChangeAdvance: change.inputChangeAdvanceAmounts?.reduce((accumulator, currentValue) => accumulator + currentValue, 0),
					inputChangeEffectiveDate: utcConverter(change.inputChangeEffectiveDate),
					upfrontLeasePayment: formData?.upfrontLeasePayment,
					initialDirectCost: formData?.initialDirectCost,
					securityDeposit: formData?.securityDeposit,
					cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
				};
			}
			return change;
		});
		const editPagePayload = {
			modificationSequence: statusData?.modificationSequence + 1,
			leaseStartDate: formData?.leaseStartDate,
			leaseTitle: formData?.leaseTitle,
			lessorName: formData?.lessorName,
			leaseTerm: formData?.leaseTerm,
			leaseEndDate: formData?.leaseEndDate,
			paymentModality: formData?.paymentModality,
			leaseType: formData?.leaseType,
			initialDiscountRate: formData?.initialDiscountRate,
			grossRentList: formData?.grossRentList,
			paymentFrequency: formData?.paymentFrequency,
			advanceAmountList: formData?.advanceAmountList,
			advanceAmount: formData?.advanceAmount,
			advanceAmounts: formData?.advanceAmounts,
			upfrontLeasePayment: formData?.upfrontLeasePayment,
			initialDirectCost: formData?.initialDirectCost,
			securityDeposit: formData?.securityDeposit,
			grossRents: formData?.grossRents,
			changes: changesPayload,
			modifiedDateTime: utcConverter(dayjs()),
			cashIncentiveAmount: formData?.cashIncentiveAmount,
			paymentModalityLabel: constantFormValues?.paymentModality?.options?.find((item) => item.value === formData?.paymentModality)?.label,
			lastModifiedDate: utcConverter(dayjs()),
			grossRentListSet: grossRentListSetData,
			advanceAmountListSet,
			effectiveDateHistory,
			advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
		};

		updateFormState('showFormSubmitModal', true);
		updateFormState('modalData.head', 'Confirmation');
		updateFormState('modalData.label', 'Do you want to update this lease record?');
		updateFormState('submitPayload', editPagePayload);
	};

	const handleModalSubmit = () => {
		updateApi({
			operation: 'update-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: formState?.submitPayload,
			_id: state._id,
			version: +singleLeaseData?.version,
		});
	};
	const hasFormDataChanged = () => {
		const currentFormData = form.getFieldsValue();

		// For VIEW mode, we need more specific field checking
		if (screen?.toLowerCase() !== 'create') {
			const currentPayload = {
				advanceAmount: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce((acc, curr) => acc + curr, 0),
				leaseTerm: currentFormData?.changes?.[0]?.inputChangeLeaseTerm,
				discountRate: currentFormData?.changes?.[0]?.inputChangeDiscountRate,
				startDay: String(currentFormData?.leaseStartDate?.date()),
				startMonth: String(currentFormData?.leaseStartDate?.month() + 1),
				startYear: String(currentFormData?.leaseStartDate?.year()),
				paymentFrequency: currentFormData?.changes?.[0]?.paymentFrequency,
				paymentTime: currentFormData?.changes?.[0]?.paymentTime,
				advanceDuration: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
				upfrontLeasePayment: currentFormData?.changes?.[0]?.upfrontLeasePayment,
				initialDirectCost: currentFormData?.changes?.[0]?.initialDirectCost,
				securityDeposit: currentFormData?.securityDeposit,
				endOfFinancialYear: currentFormData?.endOfFinancialYear,
				advanceAmounts: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts,
				grossRents: currentFormData?.changes?.[0]?.inputChangeGrossRents,
			};

			const prevPayload = {
				...previousFormData,
			};

			// Compare payloads as JSON strings
			const currentStr = JSON.stringify(currentPayload);
			const prevStr = JSON.stringify(prevPayload);

			if (currentStr !== prevStr) {
				setPreviousFormData(currentPayload);
				return true;
			}
			return false;
		}

		// For CREATE mode
		const currentPayload = {
			advanceAmount: currentFormData?.advanceAmount,
			leaseTerm: currentFormData?.leaseTerm,
			discountRate: currentFormData?.initialDiscountRate,
			startDay: String(currentFormData?.leaseStartDate?.date()),
			startMonth: String(currentFormData?.leaseStartDate?.month() + 1),
			startYear: String(currentFormData?.leaseStartDate?.year()),
			paymentFrequency: currentFormData?.paymentFrequency,
			paymentTime: currentFormData?.paymentModality,
			advanceDuration: currentFormData?.advanceAmounts?.filter((item) => item !== 0)?.length,
			upfrontLeasePayment: currentFormData?.upfrontLeasePayment,
			initialDirectCost: currentFormData?.initialDirectCost,
			securityDeposit: currentFormData?.securityDeposit,
			endOfFinancialYear: currentFormData?.endOfFinancialYear,
			advanceAmounts: currentFormData?.advanceAmounts,
			grossRents: currentFormData?.grossRents,
		};

		const currentStr = JSON.stringify(currentPayload);
		const prevStr = JSON.stringify(previousFormData);

		if (currentStr !== prevStr) {
			setPreviousFormData(currentPayload);
			return true;
		}
		return false;
	};
	// MUI Tab handler
	const selectTabHandler = (event, newValue) => {
		setSelectedTab(newValue);
		// Get the tab label and format it for URL
		// const tabLabel = tabItem[newValue]?.label.toLowerCase().replace(/\s+/g, '-');
		debouncedManageChangeRows();
		if (newValue === 1 && dataFullyLoaded) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 4 && dataFullyLoaded) {
			if (leaseDetailsShowAllRef.current?.loadData) {
				// Check if form data has changed

				leaseDetailsShowAllRef.current.loadData();
			}
		}
	};
	useEffect(() => {
		if (dataFullyLoaded) {
			if (actionButtonsRef.current?.flag?.check !== 0) {
				// Check if form data has changed
				if (hasFormDataChanged()) {
					actionButtonsRef.current.handleDataLoading();
				}
			} else {
				actionButtonsRef.current.queryV3Loader();
			}
		}
	}, [dataFullyLoaded, screen]);

	const tabItem = [
		{
			key: '1',
			label: 'Lease Details',
			children: (
				<>
					<CardAccordion
						title="Basic Information"
						darkTitle={true}
						sx={{
							mb: 2,
						}}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<GeneralLeaseInformation screen={screen} />
					</CardAccordion>

					<FinancialTermsConditions screen={screen} />
					<ActionButtons
						screen={screen}
						statusData={statusData}
						setCalculateData={setCalculateData}
						handleUpdate={handleSubmit}
						viewData={singleLeaseData}
						selectHistory={setSelectHistory}
						dataFullyLoaded={dataFullyLoaded}
						ref={actionButtonsRef}
					/>
				</>
			) || <></>,
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<>
					<IFRS16FieldsModifications screen={screen} rowIndex={rowIndex} initialChangeLength={originalChangesLength} />
					<LeaseSchedule screen={screen} viewData={singleLeaseData} dataFullyLoaded={dataFullyLoaded} ref={leaseScheduleRef} />
				</>
			),
		},
		{
			key: '3',
			label: 'Modification History',
			children: (
				<>
					<LeaseHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />
				</>
			),
		},
		{
			key: '4',
			label: 'Accounts Assignment',
			children: (
				<>
					<AccountingInformation screen={screen} identityId={singleLeaseData?._id} />
				</>
			),
		},
		{
			key: '5',
			label: 'General Ledgers',
			children: (
				<>
					<CardAccordion
						title="Lease General Leasers View"
						darkTitle={true}
						sx={{
							mt: 2,
							mb: 2,
						}}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
							fontSize: '16px !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<LeaseDetailsShowAll ref={leaseDetailsShowAllRef} />
					</CardAccordion>
				</>
			),
		},
		{
			key: '6',
			label: 'Agreements',
			children: (
				<>
					<CardAccordion
						title="Agreements"
						customHeaderSX={{
							backgroundColor: '#e1f0ff !important',
							p: '10px',
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={true}
						sx={{ mb: 2 }}
					>
						<AgreementFiles screen={screen} />
					</CardAccordion>
				</>
			),
		},
	];

	return (
		<Box>
			<Spin spinning={isUpdateLoading || isViewLoading} style={{ maxHeight: '100%' }}>
				{/*<DetailPageHeader image={solution} title="Edit Lease Record" />*/}
				<LeasePageHeader
					title="Modify Lease Record - V7"
					tabsItem={tabItem}
					selectedTab={selectedTab}
					selectTabHandler={selectTabHandler}
					createBtn={false}
				/>
				<MainCard sx={{ overflow: 'visible' }}>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						autoComplete="off"
						requiredMark={false}
						//  preserve={true}
					>
						<AntdValuePreview form={form} show={false} />
						{/* <Box sx={{ mt: 2 }}>
							{tabItem?.map((item, index) => (
								<TabPanel key={index} value={selectedTab} index={index}>
									<Box sx={{ mt: 2 }}>{tabItem[selectedTab]?.children}</Box>
								</TabPanel>
							))}
						</Box> */}
						<Box sx={{ mt: 2 }}>
							{tabItem?.map((item, index) => (
								<Box
									key={index}
									style={{
										display: selectedTab === index ? 'block' : 'none',
									}}
								>
									{item.children}
								</Box>
							))}
						</Box>
					</Form>
				</MainCard>

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						showModal={formState?.showFormSubmitModal}
						modalData={formState?.modalData}
						setShowModal={setShowFormSubmitModal}
						handleSubmit={handleModalSubmit}
					/>
				)}
				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						setSelectHistory={setSelectHistory}
						selectItem={selectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default Edit;
